import { z } from 'zod';

/**
 * Per-device kiosk identity, loaded at boot from a `config.json` sitting beside
 * the page rather than baked into the bundle.
 *
 * One build is deployed once and shared by every tablet; each tablet opens its
 * own URL (`/kiosk-1/`, `/kiosk-2/`) and picks up its own config. That is what
 * lets each kiosk report a distinct identity — which the backend turns into a
 * distinct printer via PRINT_ROLE_KIOSK_MAP. Adding a kiosk or rotating a
 * leaked token is a JSON edit, not a rebuild.
 */
const KioskConfigSchema = z.object({
  kioskId: z.string().uuid(),
  kioskToken: z.string().min(1),
});

export type KioskConfig = z.infer<typeof KioskConfigSchema>;

const CONFIG_FILENAME = 'config.json';

let loaded: KioskConfig | null = null;

/**
 * True in dev, mock-prod, and Vitest — the same gate getWsClient() uses to pick
 * the mock transport. Only these modes fall back to build-time env vars, so a
 * developer needs no config.json on disk.
 */
function envFallbackMode(): boolean {
  return (
    (__USE_MOCKS__ && import.meta.env.VITE_USE_MOCKS !== 'false') ||
    import.meta.env.MODE === 'test'
  );
}

function envConfig(): KioskConfig {
  return {
    kioskId: import.meta.env.VITE_KIOSK_ID,
    kioskToken: import.meta.env.VITE_KIOSK_TOKEN_BOOTSTRAP || 'dev-token',
  };
}

/**
 * Absolute URL of the directory containing this page, always trailing-slash —
 * `/kiosk-2/` whether the tablet is pointed at `/kiosk-2` or
 * `/kiosk-2/index.html`. Shared by the config.json fetch and the router
 * basename, since both need the same "which kiosk directory am I served
 * from" answer.
 */
export function kioskBaseUrl(): string {
  const { baseURI } = document;
  if (baseURI.endsWith('/')) return baseURI;
  const lastSegment = baseURI.slice(baseURI.lastIndexOf('/') + 1);
  const withoutFile = lastSegment.includes('.')
    ? baseURI.slice(0, baseURI.lastIndexOf('/') + 1)
    : `${baseURI}/`;
  return withoutFile;
}

/**
 * Pathname of the kiosk's own directory, for use as the router `basename`
 * (`/kiosk-2`, or `''` at the server root). Deep links like `/kiosk-2/menu`
 * only resolve if the router knows it's mounted under `/kiosk-2`.
 */
export function kioskBasePath(): string {
  const { pathname } = new URL(kioskBaseUrl());
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

/** URL of the config.json in the page's own directory, not the server root. */
export function kioskConfigUrl(): string {
  return new URL(CONFIG_FILENAME, kioskBaseUrl()).href;
}

/**
 * Loads and validates this device's identity. Must be awaited before any API
 * request or WS connection.
 *
 * In production a missing or malformed config.json is a hard failure — it must
 * never fall back to a default identity, because a tablet silently acting as
 * kiosk 1 would mis-attribute its orders and print every ticket on kiosk 1's
 * printer. That is the exact failure this module exists to prevent.
 */
export async function loadKioskConfig(): Promise<KioskConfig> {
  if (envFallbackMode()) {
    loaded = envConfig();
    return loaded;
  }

  const url = kioskConfigUrl();

  let res: Response;
  try {
    res = await fetch(url, { cache: 'no-store' });
  } catch (err) {
    throw new Error(
      `Tidak dapat memuat ${url}: ${err instanceof Error ? err.message : 'network error'}`,
    );
  }
  if (!res.ok) {
    throw new Error(`Tidak dapat memuat ${url}: HTTP ${res.status}`);
  }

  const parsed = KioskConfigSchema.safeParse(await res.json());
  if (!parsed.success) {
    throw new Error(`${url} tidak valid: ${parsed.error.issues.map((i) => i.message).join(', ')}`);
  }

  loaded = parsed.data;
  return loaded;
}

/**
 * This device's identity.
 *
 * In a production build this throws if called before loadKioskConfig() resolves
 * — a loud crash beats sending a wrong token. In dev/mock/test it resolves the
 * env fallback on demand, so unit tests that exercise a component's API calls
 * don't each have to bootstrap the config first.
 */
export function kioskConfig(): KioskConfig {
  if (!loaded) {
    if (envFallbackMode()) return envConfig();
    throw new Error('kioskConfig() called before loadKioskConfig() resolved');
  }
  return loaded;
}

/** Test-only: drops the cached config so specs can re-load a different one. */
export function resetKioskConfigForTests(): void {
  loaded = null;
}
